import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import BASE_URL from '../../api';

const API_URL = `${BASE_URL}/api/onlinebookings/mis-report`;
const CUSTOMER_API_URL = `${BASE_URL}/api/master/customer`;
const BRANCH_API_URL = `${BASE_URL}/api/master/branch`;
const AREA_API_URL = `${BASE_URL}/api/master/area`;

const MISReport = () => {
  const [form, setForm] = useState({
    fromDate: '',
    toDate: '',
    originBranch: 'All',
    billingClient: 'All',
    destinationLocation: 'All',
    type: 'All'
  });

  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    totalConsignmentNo: 0,
    totalActualWeight: 0,
    totalVolumetricWeight: 0,
    totalPcs: 0,
    totalChargeableWeight: 0
  });

  const [manifestData, setManifestData] = useState({ mapByBookingId: {}, mapByDocket: {} });
  const [manifestsList, setManifestsList] = useState([]);

  // Pagination & Search states
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  const token = () => localStorage.getItem('token');
  const [allBookings, setAllBookings] = useState([]);

  const buildManifestMap = (manifestsList, bookingsList = []) => {
    const mapByBookingId = {};
    const mapByDocket = {};

    const bookingById = {};
    const bookingByDocket = {};
    if (Array.isArray(bookingsList)) {
      bookingsList.forEach(b => {
        if (b && b._id) {
          const idStr = b._id.toString();
          bookingById[idStr] = b;
          if (b.docketNumber) {
            const doc = b.docketNumber.toString().trim();
            bookingByDocket[doc] = b;
            bookingByDocket[doc.toLowerCase()] = b;
            bookingByDocket[doc.toUpperCase()] = b;
          }
        }
      });
    }

    if (!Array.isArray(manifestsList)) return { mapByBookingId, mapByDocket };

    manifestsList.forEach(m => {
      const onlineArr = m.online || m.onlineBooking || [];
      const len = onlineArr.length;

      let remList = [];
      if (m.bookingremarks && typeof m.bookingremarks === 'string' && m.bookingremarks.trim() !== '') {
        if (m.bookingremarks.includes('|')) {
          remList = m.bookingremarks.split('|').map(s => s.trim());
        } else if (m.bookingremarks.includes(',') && len > 1) {
          remList = m.bookingremarks.split(',').map(s => s.trim());
        } else {
          remList = [m.bookingremarks.trim()];
        }
      }

      let consList = [];
      if (m.consignors && typeof m.consignors === 'string' && m.consignors.trim() !== '') {
        if (m.consignors.includes(',')) {
          consList = m.consignors.split(',').map(s => s.trim());
        } else if (m.consignors.includes('|')) {
          consList = m.consignors.split('|').map(s => s.trim());
        } else {
          consList = [m.consignors.trim()];
        }
      }

      onlineArr.forEach((b, idx) => {
        const rawId = (b && typeof b === 'object' ? b._id : b)?.toString();
        const bObj = (b && typeof b === 'object') ? b : (bookingById[rawId] || {});
        const bId = rawId || bObj._id?.toString();
        const docket = (bObj.docketNumber || (bId ? bookingById[bId]?.docketNumber : null))?.toString().trim();

        // 1. Remark for this booking
        let remark = null;
        if (idx < remList.length && remList[idx] && remList[idx] !== 'N/A' && remList[idx] !== '') {
          remark = remList[idx];
        } else if (remList.length === 1 && len <= 1 && remList[0] && remList[0] !== 'N/A') {
          remark = remList[0];
        }

        // 2. Custom consignee entered in Manifest Out table column "Consignee" (saved in m.consignors)
        let customConsignee = null;
        if (idx < consList.length && consList[idx] && consList[idx] !== 'N/A' && consList[idx] !== '') {
          customConsignee = consList[idx];
        } else if (consList.length === 1 && len <= 1 && consList[0] && consList[0] !== 'N/A') {
          customConsignee = consList[0];
        }

        const consigneeAddress = bObj.consigneeTOaddress || (bId && bookingById[bId]?.consigneeTOaddress) || null;

        const info = {
          customConsignee: customConsignee || null,
          consigneeAddress: consigneeAddress,
          remark: remark || null,
          overallRemarks: (m.remarks && m.remarks !== 'N/A' && m.remarks.trim() !== '') ? m.remarks.trim() : null,
          manifestno: m.manifestno
        };

        if (bId) {
          if (!mapByBookingId[bId] || (!mapByBookingId[bId].customConsignee && customConsignee)) {
            mapByBookingId[bId] = info;
          }
        }
        if (docket) {
          if (!mapByDocket[docket] || (!mapByDocket[docket].customConsignee && customConsignee)) {
            mapByDocket[docket] = info;
            mapByDocket[docket.toLowerCase()] = info;
            mapByDocket[docket.toUpperCase()] = info;
          }
        }
      });
    });

    // Also populate from bookingsList for any booking where consigneeTOaddress was saved directly
    if (Array.isArray(bookingsList)) {
      bookingsList.forEach(b => {
        if (b && b.consigneeTOaddress && b.consigneeTOaddress.trim() !== '' && b.consigneeTOaddress !== 'N/A') {
          const doc = b.docketNumber?.toString().trim();
          const bId = b._id?.toString();
          const addr = b.consigneeTOaddress.trim();

          if (doc) {
            if (!mapByDocket[doc]) {
              mapByDocket[doc] = { customConsignee: addr, consigneeAddress: addr };
              mapByDocket[doc.toLowerCase()] = mapByDocket[doc];
              mapByDocket[doc.toUpperCase()] = mapByDocket[doc];
            } else if (!mapByDocket[doc].customConsignee) {
              mapByDocket[doc].customConsignee = addr;
              mapByDocket[doc].consigneeAddress = addr;
            }
          }

          if (bId) {
            if (!mapByBookingId[bId]) {
              mapByBookingId[bId] = { customConsignee: addr, consigneeAddress: addr };
            } else if (!mapByBookingId[bId].customConsignee) {
              mapByBookingId[bId].customConsignee = addr;
              mapByBookingId[bId].consigneeAddress = addr;
            }
          }
        }
      });
    }

    return { mapByBookingId, mapByDocket };
  };

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [resCust, resBranch, resArea, resManifest, resBookings] = await Promise.all([
          fetch(CUSTOMER_API_URL, { headers: { Authorization: `Bearer ${token()}` } }),
          fetch(BRANCH_API_URL, { headers: { Authorization: `Bearer ${token()}` } }),
          fetch(AREA_API_URL, { headers: { Authorization: `Bearer ${token()}` } }),
          fetch(`${BASE_URL}/api/outgoingmanifests`, { headers: { Authorization: `Bearer ${token()}` } }).catch(() => null),
          fetch(`${BASE_URL}/api/onlinebookings`, { headers: { Authorization: `Bearer ${token()}` } }).catch(() => null)
        ]);
        const jsonCust = await resCust.json();
        const jsonBranch = await resBranch.json();
        const jsonArea = await resArea.json();
        
        if (jsonCust.success) setCustomers(jsonCust.data || []);
        if (jsonBranch.success) setBranches(jsonBranch.data || (Array.isArray(jsonBranch) ? jsonBranch : []));
        if (jsonArea.success) setAreas(jsonArea.data || []);

        let mList = [];
        if (resManifest) {
          const jsonManifest = await resManifest.json();
          mList = Array.isArray(jsonManifest) ? jsonManifest : (jsonManifest.data || []);
        }

        let bList = [];
        if (resBookings) {
          const jsonBookings = await resBookings.json();
          bList = Array.isArray(jsonBookings) ? jsonBookings : (jsonBookings.data || []);
        }

        if (Array.isArray(bList) && bList.length > 0) setAllBookings(bList);
        if (Array.isArray(mList) && mList.length > 0) {
          setManifestsList(mList);
          setManifestData(buildManifestMap(mList, bList));
        }
      } catch (e) {
        console.error("Error fetching masters:", e);
      }
    };
    fetchMasters();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setData([]);
    
    try {
      setCurrentPage(1); // Reset page on new search
      const queryParams = new URLSearchParams();
      if (form.fromDate) queryParams.append('fromDate', form.fromDate);
      if (form.toDate) queryParams.append('toDate', form.toDate);
      if (form.originBranch !== 'All') queryParams.append('originBranch', form.originBranch);
      if (form.billingClient !== 'All') queryParams.append('billingClient', form.billingClient);
      if (form.destinationLocation !== 'All') queryParams.append('destinationLocation', form.destinationLocation);
      if (form.type !== 'All') queryParams.append('type', form.type);

      const [res, resManifest, resBookings] = await Promise.all([
        fetch(`${API_URL}?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token()}`
          }
        }),
        fetch(`${BASE_URL}/api/outgoingmanifests`, {
          headers: {
            Authorization: `Bearer ${token()}`
          }
        }).catch(() => null),
        fetch(`${BASE_URL}/api/onlinebookings`, {
          headers: {
            Authorization: `Bearer ${token()}`
          }
        }).catch(() => null)
      ]);

      let mList = [];
      if (resManifest) {
        try {
          const jsonManifest = await resManifest.json();
          mList = Array.isArray(jsonManifest) ? jsonManifest : (jsonManifest.data || []);
        } catch (mErr) {
          console.error("Error parsing manifests:", mErr);
        }
      }

      let bList = [];
      if (resBookings) {
        try {
          const jsonBookings = await resBookings.json();
          bList = Array.isArray(jsonBookings) ? jsonBookings : (jsonBookings.data || []);
        } catch (bErr) {
          console.error("Error parsing bookings:", bErr);
        }
      }

      const activeBookings = (Array.isArray(bList) && bList.length > 0) ? bList : allBookings;
      if (Array.isArray(bList) && bList.length > 0) {
        setAllBookings(bList);
      }

      if (Array.isArray(mList) && mList.length > 0) {
        setManifestsList(mList);
        setManifestData(buildManifestMap(mList, activeBookings));
      }

      let json = null;
      try {
        json = await res.json();
      } catch (jErr) {
        console.warn("Could not parse MIS report JSON:", jErr);
      }

      let rawList = (json && json.success && Array.isArray(json.data?.list)) ? [...json.data.list] : [];
      const existingDocketSet = new Set();
      const existingBookingIdSet = new Set();

      rawList.forEach(item => {
        if (item.docket) existingDocketSet.add(item.docket.toString().trim().toUpperCase());
        const bId = (item.booingid?._id || (typeof item.booingid === 'string' ? item.booingid : null) || item._id)?.toString();
        if (bId) existingBookingIdSet.add(bId);
      });

      // 1. Merge bookings from Outgoing Manifests (mList) so recent manifests (like OGM-000160) always appear
      if (Array.isArray(mList)) {
        mList.forEach(m => {
          const onlineArr = m.online || m.onlineBooking || [];
          const consList = (m.consignors || '').split(',').map(s => s.trim());
          const remList = (m.bookingremarks || '').split('|').map(s => s.trim());

          onlineArr.forEach((b, idx) => {
            const rawId = (b && typeof b === 'object' ? b._id : b)?.toString();
            const docNum = (b && typeof b === 'object' ? b.docketNumber : null)?.toString().trim();

            if (rawId && existingBookingIdSet.has(rawId)) return;
            if (docNum && existingDocketSet.has(docNum.toUpperCase())) return;

            const bObj = (b && typeof b === 'object' && b.docketNumber) 
              ? b 
              : (rawId ? activeBookings.find(ab => ab._id?.toString() === rawId) : null) || (typeof b === 'object' ? b : {});

            const docket = (docNum || bObj.docketNumber || '').toString().trim();
            const bId = rawId || bObj._id?.toString();

            const customConsignee = (idx < consList.length && consList[idx] && consList[idx] !== 'N/A' && consList[idx] !== '')
              ? consList[idx]
              : (consList.length === 1 && consList[0] && consList[0] !== 'N/A' ? consList[0] : null);

            const remark = (idx < remList.length && remList[idx] && remList[idx] !== 'N/A' && remList[idx] !== '')
              ? remList[idx]
              : (remList.length === 1 && remList[0] && remList[0] !== 'N/A' ? remList[0] : null);

            // Date filtering: check bookingdate, createdAt, ogdate, or manifest createdAt
            if (form.fromDate || form.toDate) {
              const bDate = new Date(bObj.bookingdate || bObj.createdAt);
              const mDate = new Date(m.ogdate || m.createdAt);
              const mCreated = new Date(m.createdAt);

              const start = form.fromDate ? new Date(`${form.fromDate}T00:00:00.000`) : null;
              const end = form.toDate ? new Date(`${form.toDate}T23:59:59.999`) : null;

              const matchBDate = !isNaN(bDate.getTime()) && (!start || bDate >= start) && (!end || bDate <= end);
              const matchMDate = !isNaN(mDate.getTime()) && (!start || mDate >= start) && (!end || mDate <= end);
              const matchMCreated = !isNaN(mCreated.getTime()) && (!start || mCreated >= start) && (!end || mCreated <= end);

              if (!matchBDate && !matchMDate && !matchMCreated) return;
            }

            // Billing client filter
            if (form.billingClient && form.billingClient !== 'All') {
              const cId = (bObj.consigneeid?._id || bObj.consigneeid)?.toString();
              const clientCustId = (bObj.pickrequestid?.clientid?._id || bObj.pickrequestid?.clientid)?.toString();
              const consignorCustId = (bObj.consignorid?._id || bObj.consignorid)?.toString();

              if (cId !== form.billingClient && clientCustId !== form.billingClient && consignorCustId !== form.billingClient) {
                return;
              }
            }

            // Origin branch filter
            if (form.originBranch && form.originBranch !== 'All') {
              const origId = (bObj.pickrequestid?.locationid?._id || bObj.pickrequestid?.locationid || m.routeid?.originid?._id || m.routeid?.originid)?.toString();
              if (origId && origId !== form.originBranch) return;
            }

            // Destination location filter
            if (form.destinationLocation && form.destinationLocation !== 'All') {
              const destId = (m.desthubid?._id || m.routeid?.destinationid?._id || bObj.locationid?._id || bObj.locationid)?.toString();
              if (destId && destId !== form.destinationLocation) return;
            }

            const syntheticItem = {
              _id: bId || docket,
              docket: docket || 'N/A',
              bookingdate: bObj.bookingdate || m.ogdate || m.createdAt,
              createddate: bObj.createdAt || m.createdAt,
              booingid: bObj,
              consignorid: bObj.pickrequestid?.clientid || bObj.consignorid || null,
              consigneeid: bObj.consigneeid || null,
              consigneeTOaddress: customConsignee || bObj.consigneeTOaddress || '',
              manifestConsignee: customConsignee || null,
              manifestRemarks: remark || (m.remarks && m.remarks !== 'N/A' ? m.remarks : null),
              bookingremarks: remark || bObj.bookingremarks || null,
              manifestno: m.manifestno,
              originlocation: bObj.pickrequestid?.locationid || m.routeid?.originid || null,
              destlocation: m.desthubid ? {
                _id: m.desthubid._id,
                areaname: m.desthubid.branchname,
                hub: m.desthubid.branchcode
              } : (m.routeid?.destinationid || bObj.locationid || null),
              pincode: bObj.topincode?.pincode || '',
              actweight: bObj.actweight || 0,
              volweight: bObj.volweight || 0,
              grandtotal: bObj.invoicevalue || 0,
              noofpiece: bObj.noofbox || 0,
              docketstatus: bObj.status || m.status || 'Manifest Out - Intransit',
              createdAt: bObj.createdAt || m.createdAt
            };

            rawList.push(syntheticItem);
            if (docket) existingDocketSet.add(docket.toUpperCase());
            if (bId) existingBookingIdSet.add(bId);
          });
        });
      }

      // 2. Merge any other activeBookings matching filters
      if (Array.isArray(activeBookings)) {
        activeBookings.forEach(b => {
          if (!b || !b.docketNumber) return;
          const docket = b.docketNumber.toString().trim();
          const bId = b._id?.toString();

          if (existingDocketSet.has(docket.toUpperCase())) return;
          if (bId && existingBookingIdSet.has(bId)) return;

          // Date filter
          if (form.fromDate || form.toDate) {
            const bDate = new Date(b.bookingdate || b.createdAt);
            const start = form.fromDate ? new Date(`${form.fromDate}T00:00:00.000`) : null;
            const end = form.toDate ? new Date(`${form.toDate}T23:59:59.999`) : null;

            if (isNaN(bDate.getTime()) || (start && bDate < start) || (end && bDate > end)) return;
          }

          // Billing client filter
          if (form.billingClient && form.billingClient !== 'All') {
            const cId = (b.consigneeid?._id || b.consigneeid)?.toString();
            const clientCustId = (b.pickrequestid?.clientid?._id || b.pickrequestid?.clientid)?.toString();
            const consignorCustId = (b.consignorid?._id || b.consignorid)?.toString();

            if (cId !== form.billingClient && clientCustId !== form.billingClient && consignorCustId !== form.billingClient) {
              return;
            }
          }

          // Origin branch filter
          if (form.originBranch && form.originBranch !== 'All') {
            const origId = (b.pickrequestid?.locationid?._id || b.pickrequestid?.locationid)?.toString();
            if (origId && origId !== form.originBranch) return;
          }

          // Destination location filter
          if (form.destinationLocation && form.destinationLocation !== 'All') {
            const destId = (b.locationid?._id || b.locationid)?.toString();
            if (destId && destId !== form.destinationLocation) return;
          }

          rawList.push({
            _id: bId || docket,
            docket: docket,
            bookingdate: b.bookingdate || b.createdAt,
            createddate: b.createdAt,
            booingid: b,
            consignorid: b.pickrequestid?.clientid || b.consignorid || null,
            consigneeid: b.consigneeid || null,
            consigneeTOaddress: b.consigneeTOaddress || '',
            originlocation: b.pickrequestid?.locationid || null,
            destlocation: b.locationid || null,
            pincode: b.topincode?.pincode || '',
            actweight: b.actweight || 0,
            volweight: b.volweight || 0,
            grandtotal: b.invoicevalue || 0,
            noofpiece: b.noofbox || 0,
            docketstatus: b.status || 'Initiated',
            createdAt: b.createdAt
          });

          existingDocketSet.add(docket.toUpperCase());
          if (bId) existingBookingIdSet.add(bId);
        });
      }

      // Compute total summary
      let totalActualWeight = 0;
      let totalVolumetricWeight = 0;
      let totalPcs = 0;
      let totalChargeableWeight = 0;

      rawList.forEach(item => {
        const act = Number(item.actweight) || 0;
        const vol = Number(item.volweight) || 0;
        const pcs = Number(item.noofpiece || item.booingid?.noofbox) || 0;
        totalActualWeight += act;
        totalVolumetricWeight += vol;
        totalPcs += pcs;
        totalChargeableWeight += Math.max(act, vol);
      });

      setData(rawList);
      setSummary({
        totalConsignmentNo: rawList.length,
        totalActualWeight,
        totalVolumetricWeight,
        totalPcs,
        totalChargeableWeight
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getConsignorObj = (item) => {
    return item.consignorid || 
           item.booingid?.pickrequestid?.clientid || 
           item.booingid?.consignorid || 
           item.booingid?.clientid || 
           item.pickrequestid?.clientid || 
           item.clientid;
  };

  const getConsignorName = (item) => {
    // 1. Check customer object (Customer is the Consignor)
    const cObj = getConsignorObj(item);
    if (cObj && typeof cObj === 'object') {
      const name = cObj.companyName || cObj.name || cObj.clientname || cObj.customername;
      if (name && name.trim() !== '') return name.trim();
    }

    // 2. Lookup customer ID in customers master list
    const cId = (cObj && typeof cObj === 'object' ? cObj._id : cObj) || item.consignorid;
    if (cId && customers.length > 0) {
      const match = customers.find(c => String(c._id) === String(cId));
      if (match) {
        const name = match.companyName || match.name;
        if (name && name.trim() !== '') return name.trim();
      }
    }

    // 3. Lookup from allBookings if docket matches (Customer is clientid)
    if (item.docket && allBookings.length > 0) {
      const match = allBookings.find(b => b.docketNumber && b.docketNumber.toString().trim().toUpperCase() === item.docket.toString().trim().toUpperCase());
      if (match?.pickrequestid?.clientid) {
        const client = match.pickrequestid.clientid;
        const name = (typeof client === 'object' ? (client.companyName || client.name) : null);
        if (name && name.trim() !== '') return name.trim();
        if (typeof client === 'string' && customers.length > 0) {
          const cMatch = customers.find(c => String(c._id) === String(client));
          if (cMatch) return cMatch.companyName || cMatch.name || '-';
        }
      }
    }

    // 4. Direct string fields
    if (item.clientname && typeof item.clientname === 'string' && item.clientname.trim() !== '') return item.clientname.trim();
    if (item.consignor && typeof item.consignor === 'string' && item.consignor.trim() !== '') return item.consignor.trim();
    if (item.consignorname && typeof item.consignorname === 'string' && item.consignorname.trim() !== '') return item.consignorname.trim();
    if (item.consignorName && typeof item.consignorName === 'string' && item.consignorName.trim() !== '') return item.consignorName.trim();
    if (item.booingid?.consignor && typeof item.booingid.consignor === 'string' && item.booingid.consignor.trim() !== '') return item.booingid.consignor.trim();

    // 5. If cObj is a plain string name (not ObjectId)
    if (typeof cObj === 'string' && cObj.trim() !== '' && cObj.length !== 24) {
      return cObj.trim();
    }

    return '-';
  };

  const getConsignorDetails = (item) => {
    const cObj = getConsignorObj(item);
    let matched = null;
    if (cObj && typeof cObj === 'object' && (cObj.companyName || cObj.name)) {
      matched = cObj;
    } else {
      const cId = (cObj && typeof cObj === 'object' ? cObj._id : cObj) || item.consignorid;
      if (cId && customers.length > 0) {
        matched = customers.find(c => String(c._id) === String(cId));
      }
    }

    if (matched) {
      const parts = [];
      if (matched.companyName) parts.push(`Company: ${matched.companyName}`);
      if (matched.name && matched.name !== matched.companyName) parts.push(`Contact: ${matched.name}`);
      if (matched.mobileNumber) parts.push(`Phone: ${matched.mobileNumber}`);
      if (matched.address || matched.deliveryAddress) parts.push(`Address: ${matched.address || matched.deliveryAddress}`);
      return parts.join('\n');
    }
    return getConsignorName(item);
  };

  const getConsigneeObj = (item) => {
    return item.booingid?.consigneeid || item.consigneeid;
  };

  const getManifestInfo = (item) => {
    const docket = (item.docket || item.docketno || item.docketNumber || item.booingid?.docketNumber || '')?.toString().trim();
    const bookingId = (item.booingid?._id || item.booingid || item._id)?.toString();

    let info = null;
    if (docket) {
      info = manifestData.mapByDocket[docket] || 
             manifestData.mapByDocket[docket.toLowerCase()] || 
             manifestData.mapByDocket[docket.toUpperCase()];
    }
    if (!info && bookingId) {
      info = manifestData.mapByBookingId[bookingId];
    }
    // Fallback: check allBookings directly for consigneeTOaddress
    if (!info && docket && allBookings.length > 0) {
      const match = allBookings.find(b => b.docketNumber && b.docketNumber.toString().trim().toUpperCase() === docket.toUpperCase());
      if (match && match.consigneeTOaddress && match.consigneeTOaddress.trim() !== '' && match.consigneeTOaddress !== 'N/A') {
        info = { customConsignee: match.consigneeTOaddress.trim(), consigneeAddress: match.consigneeTOaddress.trim() };
      }
    }
    if (!info && bookingId && allBookings.length > 0) {
      const match = allBookings.find(b => b._id && b._id.toString() === bookingId);
      if (match && match.consigneeTOaddress && match.consigneeTOaddress.trim() !== '' && match.consigneeTOaddress !== 'N/A') {
        info = { customConsignee: match.consigneeTOaddress.trim(), consigneeAddress: match.consigneeTOaddress.trim() };
      }
    }
    return info;
  };

  const getConsigneeFromManifest = (item) => {
    const consignorName = getConsignorName(item);
    const docket = (item.docket || item.docketno || item.docketNumber || item.booingid?.docketNumber || '')?.toString().trim().toUpperCase();
    const candidateIds = [
      item.booingid?._id?.toString(),
      (typeof item.booingid === 'string' && item.booingid.length === 24 ? item.booingid : null),
      item.bookingid?.toString(),
      item.bookingId?.toString(),
      (item._id && typeof item._id === 'string' && item._id.length === 24 ? item._id : null)
    ].filter(Boolean);

    // 1. PRIMARY: Search manifestsList (from GET /api/outgoingmanifests, e.g. "consignors": "dhoom")
    // Reverse manifests so the latest created outgoing manifest is matched first
    if (Array.isArray(manifestsList)) {
      const reversedManifests = [...manifestsList].reverse();
      for (const m of reversedManifests) {
        const onlineArr = m.online || m.onlineBooking || [];
        if (!onlineArr || onlineArr.length === 0) continue;

        let matchIdx = -1;

        // Match by any candidate booking ID in manifest.online
        for (const cId of candidateIds) {
          if (matchIdx !== -1) break;
          matchIdx = onlineArr.findIndex(b => {
            const id = (b && typeof b === 'object' ? b._id : b)?.toString();
            return id === cId;
          });
        }

        // Match by docket number in manifest.online
        if (matchIdx === -1 && docket) {
          matchIdx = onlineArr.findIndex(b => {
            const doc = (b && typeof b === 'object' ? b.docketNumber : null)?.toString().trim().toUpperCase();
            return doc === docket;
          });
        }

        // Match by looking up docket in allBookings to get its booking ID
        if (matchIdx === -1 && docket && allBookings.length > 0) {
          const bInAll = allBookings.find(ab => ab.docketNumber && ab.docketNumber.toString().trim().toUpperCase() === docket);
          if (bInAll && bInAll._id) {
            const targetId = bInAll._id.toString();
            matchIdx = onlineArr.findIndex(b => {
              const id = (b && typeof b === 'object' ? b._id : b)?.toString();
              return id === targetId;
            });
          }
        }

        // Match by looking up manifest online booking IDs in allBookings to get their docket
        if (matchIdx === -1 && docket && allBookings.length > 0) {
          matchIdx = onlineArr.findIndex(b => {
            const id = (b && typeof b === 'object' ? b._id : b)?.toString();
            if (!id) return false;
            const found = allBookings.find(ab => ab._id && ab._id.toString() === id);
            return found && found.docketNumber && found.docketNumber.toString().trim().toUpperCase() === docket;
          });
        }

        if (matchIdx !== -1) {
          // Found matching booking in this manifest!
          // Extract from m.consignors (e.g. "consignors": "dhoom")
          if (m.consignors && typeof m.consignors === 'string' && m.consignors.trim() !== '') {
            const consList = m.consignors.split(',').map(s => s.trim());
            const cName = (matchIdx < consList.length && consList[matchIdx] && consList[matchIdx] !== 'N/A' && consList[matchIdx] !== '')
              ? consList[matchIdx]
              : (consList.length === 1 && consList[0] && consList[0] !== 'N/A' ? consList[0] : null);

            if (cName && cName.trim() !== '' && cName.trim() !== 'N/A') {
              if (!consignorName || cName.trim().toLowerCase() !== consignorName.toLowerCase()) {
                return cName.trim();
              }
            }
          }

          // Extract from m.bookingremarks
          if (m.bookingremarks && typeof m.bookingremarks === 'string' && m.bookingremarks.trim() !== '') {
            const remList = m.bookingremarks.split('|').map(s => s.trim());
            const rem = (matchIdx < remList.length && remList[matchIdx] && remList[matchIdx] !== 'N/A' && remList[matchIdx] !== '')
              ? remList[matchIdx]
              : (remList.length === 1 && remList[0] && remList[0] !== 'N/A' ? remList[0] : null);

            if (rem && rem.trim() !== '' && rem.trim() !== 'N/A') {
              if (!consignorName || rem.trim().toLowerCase() !== consignorName.toLowerCase()) {
                return rem.trim();
              }
            }
          }

          // Extract from m.remarks
          if (m.remarks && typeof m.remarks === 'string' && m.remarks.trim() !== '' && m.remarks !== 'N/A') {
            if (!consignorName || m.remarks.trim().toLowerCase() !== consignorName.toLowerCase()) {
              return m.remarks.trim();
            }
          }
        }
      }
    }

    // 2. Direct fields from backend if present
    const checkDirectField = (val) => {
      if (val && typeof val === 'string' && val.trim() !== '' && val !== 'N/A') {
        if (!consignorName || val.trim().toLowerCase() !== consignorName.toLowerCase()) {
          return val.trim();
        }
      }
      return null;
    };

    const directManifestConsignee = checkDirectField(item.manifestConsignee);
    if (directManifestConsignee) return directManifestConsignee;

    const directConsigneeTO = checkDirectField(item.consigneeTOaddress) || checkDirectField(item.booingid?.consigneeTOaddress);
    if (directConsigneeTO) return directConsigneeTO;

    // 3. Fallback to manifestData maps
    if (docket && manifestData.mapByDocket[docket]?.customConsignee) {
      const val = manifestData.mapByDocket[docket].customConsignee;
      if (!consignorName || val.toLowerCase() !== consignorName.toLowerCase()) return val;
    }
    for (const cId of candidateIds) {
      if (manifestData.mapByBookingId[cId]?.customConsignee) {
        const val = manifestData.mapByBookingId[cId].customConsignee;
        if (!consignorName || val.toLowerCase() !== consignorName.toLowerCase()) return val;
      }
    }

    // 4. Fallback to allBookings consigneeTOaddress
    if (docket && allBookings.length > 0) {
      const bInAll = allBookings.find(ab => ab.docketNumber && ab.docketNumber.toString().trim().toUpperCase() === docket);
      const val = checkDirectField(bInAll?.consigneeTOaddress);
      if (val) return val;
    }
    for (const cId of candidateIds) {
      const bInAll = allBookings.find(ab => ab._id && ab._id.toString() === cId);
      const val = checkDirectField(bInAll?.consigneeTOaddress);
      if (val) return val;
    }

    return null;
  };

  const getConsigneeName = (item) => {
    const consignorName = getConsignorName(item);

    // 1. PRIMARY PRIORITY: The name added in the Outgoing Form "Consignee" field
    // (User explicitly specified: "consignors": "dhoom", this data was show on reports consignee)
    const outgoingConsignee = getConsigneeFromManifest(item);
    if (outgoingConsignee && typeof outgoingConsignee === 'string' && outgoingConsignee.trim() !== '' && outgoingConsignee.trim() !== 'N/A') {
      return outgoingConsignee.trim();
    }

    // 2. Direct string fields on item or booking
    const directConsignee = item.consignee || item.consigneename || item.consigneeName || item.booingid?.consignee || item.booingid?.consigneename || item.booingid?.consigneeName;
    if (directConsignee && typeof directConsignee === 'string' && directConsignee.trim() !== '' && directConsignee.trim() !== 'N/A') {
      if (!consignorName || directConsignee.trim().toLowerCase() !== consignorName.toLowerCase()) {
        return directConsignee.trim();
      }
    }

    // 3. Populated consignee objects from customer master (ONLY if DIFFERENT from consignor)
    const cObj = getConsigneeObj(item);
    let populatedName = null;
    if (cObj && typeof cObj === 'object') {
      populatedName = cObj.companyName || cObj.name;
    }
    if (!populatedName) {
      const cId = (cObj && typeof cObj === 'object' ? cObj._id : cObj) || item.consigneeid;
      if (cId && customers.length > 0) {
        const match = customers.find(c => String(c._id) === String(cId));
        if (match) {
          populatedName = match.companyName || match.name;
        }
      }
    }

    if (populatedName && populatedName.trim() !== '') {
      if (!consignorName || populatedName.trim().toLowerCase() !== consignorName.toLowerCase()) {
        return populatedName.trim();
      }
      if (cObj && typeof cObj === 'object' && cObj.name && cObj.name.trim().toLowerCase() !== consignorName.toLowerCase()) {
        return cObj.name.trim();
      }
      if (cObj && typeof cObj === 'object' && cObj.deliveryAddress && cObj.deliveryAddress.trim() !== '') {
        return cObj.deliveryAddress.trim();
      }
    }

    // 4. NEVER return the same name as consignor
    return '-';
  };

  const getConsigneeDetails = (item) => {
    const mInfo = getManifestInfo(item);
    const manifestRemarks = mInfo?.remark || item.manifestRemarks || item.bookingremarks || mInfo?.overallRemarks;
    const customConsignee = mInfo?.customConsignee;
    const consigneeName = getConsigneeName(item);
    const phone = getConsigneePhone(item);

    const parts = [];
    if (manifestRemarks) parts.push(`Remarks: ${manifestRemarks}`);
    if (customConsignee && customConsignee !== manifestRemarks) parts.push(`Consignee: ${customConsignee}`);
    if (consigneeName && consigneeName !== '-' && consigneeName !== manifestRemarks && consigneeName !== customConsignee) {
      parts.push(`Consignee Name: ${consigneeName}`);
    }
    if (phone && phone !== '-') parts.push(`Phone: ${phone}`);
    if (mInfo?.manifestno) parts.push(`Manifest No: ${mInfo.manifestno}`);

    const cObj = getConsigneeObj(item);
    if (cObj && typeof cObj === 'object') {
      if (cObj.deliveryAddress || cObj.address) parts.push(`Address: ${cObj.deliveryAddress || cObj.address}`);
    }

    return parts.length > 0 ? parts.join('\n') : consigneeName;
  };

  const getConsigneePhone = (item) => {
    // 1. Direct phone fields on item or booking
    if (item.consigneephone) return item.consigneephone;
    if (item.consigneemobile) return item.consigneemobile;
    if (item.booingid?.consigneephone) return item.booingid.consigneephone;
    if (item.booingid?.consigneemobile) return item.booingid.consigneemobile;

    const mInfo = getManifestInfo(item);

    // 2. Extract phone number from Manifest Out Remarks if present (e.g. 10-digit Indian phone)
    const manifestRemarks = mInfo?.remark || 
                            item.manifestRemarks || 
                            item.bookingremarks || 
                            item.booingid?.bookingremarks || 
                            mInfo?.overallRemarks;

    if (manifestRemarks && typeof manifestRemarks === 'string') {
      const phoneMatch = manifestRemarks.match(/(?:\+91|0)?[6-9]\d{9}/);
      if (phoneMatch) {
        return phoneMatch[0];
      }
    }

    const customConsignee = mInfo?.customConsignee || item.consigneeTOaddress;
    if (customConsignee && typeof customConsignee === 'string') {
      const match = customConsignee.match(/(?:\+91|0)?[6-9]\d{9}/);
      if (match) return match[0];
    }

    // 3. Populated consignee object - ONLY if not identical to consignor!
    const consignorName = getConsignorName(item);
    const consigneeName = getConsigneeName(item);

    const cObj = getConsigneeObj(item);
    if (cObj && typeof cObj === 'object') {
      const cName = cObj.companyName || cObj.name;
      // If this customer object has the same name as the consignor, but the consignee is different or remarks exist, don't show consignor's phone!
      if (!consignorName || !cName || cName.toLowerCase() !== consignorName.toLowerCase() || consigneeName === cName) {
        const phone = cObj.mobileNumber || cObj.phone || cObj.mobileNo || cObj.landLine;
        if (phone) return phone;
      }
    }

    // 4. Lookup in customers master
    const cId = (cObj && typeof cObj === 'object' ? cObj._id : cObj) || item.consigneeid;
    if (cId && customers.length > 0) {
      const match = customers.find(c => String(c._id) === String(cId));
      if (match) {
        const cName = match.companyName || match.name;
        if (!consignorName || !cName || cName.toLowerCase() !== consignorName.toLowerCase() || consigneeName === cName) {
          const phone = match.mobileNumber || match.phone || match.mobileNo || match.landLine;
          if (phone) return phone;
        }
      }
    }

    return '-';
  };

  const getDeliveryStatus = (item) => {
    if (item.docketstatus === 'Completed' || item.booingid?.status === 'Delivered') {
      return 'Delivered';
    }
    return item.booingid?.status || item.docketstatus || '-';
  };

  const getBranchName = (item) => {
    // 1. Check direct populated branch on consignorid
    if (item.consignorid?.branch) {
      if (typeof item.consignorid.branch === 'object' && item.consignorid.branch.branchname) {
        return item.consignorid.branch.branchname;
      }
      const bObj = branches.find(b => b._id === item.consignorid.branch || b._id === item.consignorid.branch._id);
      if (bObj) return bObj.branchname;
    }

    // 2. Direct branch property if present
    if (item.customerbranch?.branchname) return item.customerbranch.branchname;
    if (item.branchid?.branchname) return item.branchid.branchname;

    // 3. Check hub on originlocation area
    if (item.originlocation?.hub && item.originlocation.hub.trim() !== '') {
      const bObj = branches.find(
        b => b.branchcode?.toLowerCase() === item.originlocation.hub.toLowerCase() ||
             b.branchname?.toLowerCase() === item.originlocation.hub.toLowerCase()
      );
      if (bObj) return bObj.branchname;
      return item.originlocation.hub;
    }

    // 4. Check if originlocation areaname matches any branch name
    if (item.originlocation?.areaname) {
      const bObj = branches.find(
        b => b.branchname?.toLowerCase() === item.originlocation.areaname.toLowerCase()
      );
      if (bObj) return bObj.branchname;
    }

    // 5. Fallback to originlocation areaname or '-'
    return item.originlocation?.areaname || '-';
  };

  const handleExport = () => {
    const exportData = filteredData.map((item, index) => ({
      'S NO': index + 1,
      'Docket No': item.docket || '-',
      'Docket Date': item.bookingdate ? new Date(item.bookingdate).toLocaleDateString('en-GB') : '-',
      'Branch Name': getBranchName(item),
      'Consignor Name': getConsignorName(item),
      'Consignee Name': getConsigneeName(item),
      'Consignee PhoneNo': getConsigneePhone(item),
      'Destination': item.destlocation?.areaname || '-',
      'Pincode': item.booingid?.topincode?.pincode || item.pincode || '-',
      'Pcs': item.noofpiece || item.booingid?.noofbox || 0,
      'Estimate Date Of Delivery': '-',
      'Delivery Status': getDeliveryStatus(item),
      'TAT': '-',
      'Weight': item.actweight || 0,
      'Eway Bill No': item.booingid?.ewaybill || '-', 
      'Invoice Value': item.grandtotal || 0
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MIS_Report');
    XLSX.writeFile(wb, 'MIS_Report.xlsx');
  };

  // Filter by searchTerm if provided
  const filteredData = data.filter(item => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const docket = (item.docket || '').toString().toLowerCase();
    const consignor = getConsignorName(item).toLowerCase();
    const consignee = getConsigneeName(item).toLowerCase();
    const manifestno = (item.manifestno || '').toLowerCase();
    return docket.includes(term) || consignor.includes(term) || consignee.includes(term) || manifestno.includes(term);
  });

  // Pagination calculation
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const hubOptions = [
    { value: 'All', label: 'All' },
    ...branches.map(b => ({ value: b._id, label: b.branchcode ? `${b.branchname} (${b.branchcode})` : b.branchname })),
    ...areas.filter(a => !branches.some(b => b.branchname?.toLowerCase() === a.areaname?.toLowerCase()))
      .map(a => ({ value: a._id, label: a.areaname }))
  ];

  return (
    <div className="p-4 bg-gray-50 min-h-screen font-sans text-sm">
      <div className="bg-white p-4 shadow mb-4">
        <h2 className="text-center font-bold text-lg mb-4">MIS REPORT</h2>
        
        <div className="flex flex-wrap md:flex-nowrap gap-6">
          {/* Filters Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full md:w-2/3">
            <div>
              <label className="block text-gray-600 mb-1">From Date</label>
              <input type="date" className="border p-2 w-full rounded" 
                value={form.fromDate} onChange={e => setForm({...form, fromDate: e.target.value})} />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">To date</label>
              <input type="date" className="border p-2 w-full rounded" 
                value={form.toDate} onChange={e => setForm({...form, toDate: e.target.value})} />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Origin Branch</label>
              <Select
                options={hubOptions}
                value={hubOptions.find(opt => opt.value === form.originBranch)}
                onChange={val => setForm({ ...form, originBranch: val ? val.value : 'All' })}
                isClearable
                placeholder="Select or type..."
                className="text-sm"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Billing Client</label>
              <select className="border p-2 w-full rounded" 
                value={form.billingClient} onChange={e => setForm({...form, billingClient: e.target.value})}>
                <option value="All">All</option>
                {customers.map(c => <option key={c._id} value={c._id}>{c.companyName || c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Destination Location</label>
              <Select
                options={hubOptions}
                value={hubOptions.find(opt => opt.value === form.destinationLocation)}
                onChange={val => setForm({ ...form, destinationLocation: val ? val.value : 'All' })}
                isClearable
                placeholder="Select or type..."
                className="text-sm"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Type</label>
              <select className="border p-2 w-full rounded" 
                value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                <option value="All">All</option>
                <option value="INWARD">INWARD</option>
                <option value="OUTWARD">OUTWARD</option>
                <option value="DELIVERY">DELIVERY</option>
                <option value="NON_DELIVERY">NON_DELIVERY</option>
                <option value="POD_UPDATED">POD_UPDATED</option>
                <option value="POD_PENDING">POD_PENDING</option>
                <option value="SHIPMENT BOOKED">SHIPMENT BOOKED</option>
              </select>
            </div>
          </div>
          
          {/* Summary Section */}
          <div className="w-full md:w-1/3">
            <div className="border rounded">
              <div className="bg-blue-600 text-white text-center py-1 font-bold">SUMMARY</div>
              <div className="p-4 grid grid-cols-2 gap-y-2 text-gray-700">
                <div>Total Consignment No</div>
                <div className="text-right text-blue-600 font-bold bg-blue-100 rounded px-2 w-max ml-auto">
                  {summary.totalConsignmentNo}
                </div>
                <div>Total Actual Weight</div>
                <div className="text-right text-blue-600 font-bold bg-blue-100 rounded px-2 w-max ml-auto">
                  {summary.totalActualWeight.toFixed(2)}
                </div>
                <div>Total Volumetric Weight</div>
                <div className="text-right text-blue-600 font-bold bg-blue-100 rounded px-2 w-max ml-auto">
                  {summary.totalVolumetricWeight.toFixed(2)}
                </div>
                <div>Total Pcs</div>
                <div className="text-right text-blue-600 font-bold bg-blue-100 rounded px-2 w-max ml-auto">
                  {summary.totalPcs.toFixed(2)}
                </div>
                <div>Total Chargeable Weight</div>
                <div className="text-right text-blue-600 font-bold bg-blue-100 rounded px-2 w-max ml-auto">
                  {summary.totalChargeableWeight.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-4">
            <button onClick={fetchReport} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full font-bold shadow transition-all">
              {loading ? 'Loading...' : 'Show'}
            </button>
            <button onClick={handleExport} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full font-bold shadow transition-all">
              Export
            </button>
          </div>
          <div>
            <input 
              type="text" 
              placeholder="Search Docket, Consignee, Consignor..." 
              value={searchTerm} 
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="border p-2 rounded w-72 text-sm shadow-sm" 
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-x-auto border">
        <table className="w-full text-left whitespace-nowrap min-w-max border-collapse">
          <thead>
            <tr className="bg-blue-600 text-white">
              <th className="p-2 border">S NO</th>
              <th className="p-2 border">Docket No</th>
              <th className="p-2 border">Docket Date</th>
              <th className="p-2 border">Branch Name</th>
              <th className="p-2 border">Consignor Name</th>
              <th className="p-2 border">Consignee Name</th>
              <th className="p-2 border">Consignee PhoneNo</th>
              <th className="p-2 border">Destination</th>
              <th className="p-2 border">Pincode</th>
              <th className="p-2 border">Pcs</th>
              <th className="p-2 border">Estimate Date Of Delivery</th>
              <th className="p-2 border">Delivery Status</th>
              <th className="p-2 border">TAT</th>
              <th className="p-2 border">Weight</th>
              <th className="p-2 border">Eway Bill No</th>
              <th className="p-2 border">Invoice Value</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr><td colSpan={16} className="text-center py-4 border text-gray-500">No records found</td></tr>
            ) : (
              paginatedData.map((item, index) => (
                <tr key={item._id} className="hover:bg-gray-50 text-gray-700 text-xs">
                  <td className="p-2 border text-center">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td className="p-2 border">{item.docket || '-'}</td>
                  <td className="p-2 border">{item.bookingdate ? new Date(item.bookingdate).toLocaleDateString('en-GB') : '-'}</td>
                  <td className="p-2 border">{getBranchName(item)}</td>
                  <td className="p-2 border max-w-[200px] truncate" title={getConsignorDetails(item)}>
                     {getConsignorName(item)}
                  </td>
                  <td className="p-2 border max-w-[200px] truncate" title={getConsigneeDetails(item)}>
                    {getConsigneeName(item)}
                  </td>
                  <td className="p-2 border">{getConsigneePhone(item)}</td>
                  <td className="p-2 border">{item.destlocation?.areaname || '-'}</td>
                  <td className="p-2 border">{item.booingid?.topincode?.pincode || item.pincode || '-'}</td>
                  <td className="p-2 border text-center">{item.noofpiece || item.booingid?.noofbox || 0}</td>
                  <td className="p-2 border text-center">-</td>
                  <td className="p-2 border max-w-[150px] whitespace-normal">{getDeliveryStatus(item)}</td>
                  <td className="p-2 border text-center">-</td>
                  <td className="p-2 border text-center">{(item.actweight || 0).toFixed(2)}</td>
                  <td className="p-2 border">{item.booingid?.ewaybill || '-'}</td>
                  <td className="p-2 border text-center">{item.grandtotal || 0}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <span className="text-sm text-gray-600 font-medium">
            Showing <span className="text-blue-600">{Math.min(data.length, (currentPage - 1) * itemsPerPage + 1)}</span> to <span className="text-blue-600">{Math.min(data.length, currentPage * itemsPerPage)}</span> of {data.length} entries
          </span>
          <div className="flex gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition">
              Previous
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MISReport;
